import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileSignature, FileText, Download, Upload, Send, PenLine, CheckCircle2,
  Clock, Lock, AlertTriangle, X, Plus, User as UserIcon, Filter, RefreshCw, Trash2, Sparkles, Inbox,
  Undo2, ExternalLink, LayoutGrid, List as ListIcon
} from "lucide-react";

export function TemplatesGrid({ templates }) {
  const byCat = useMemo(() => {
    const out = {};
    for (const t of templates) { (out[t.category] ||= []).push(t); }
    return out;
  }, [templates]);
  const BULK_KEYS = new Set(["offer_letter", "appointment_letter", "increment_letter"]);
  const downloadTemplate = async (key) => {
    try {
      const res = await api.get(`/hr/documents/bulk/template-xlsx?template_key=${key}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${key}_bulk_template.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Template download failed");
    }
  };
  const uploadFilledRef = React.useRef({});
  const bulkUpload = async (key, autoPdf) => {
    const inp = uploadFilledRef.current[key];
    if (!inp) return;
    inp.dataset.autoPdf = autoPdf ? "1" : "0";
    inp.click();
  };
  const onUploadPicked = async (key, evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    const autoPdf = evt.target.dataset.autoPdf === "1";
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/hr/documents/bulk-generate?template_key=${key}&auto_generate_pdf=${autoPdf}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      const c = data.created_count || 0;
      const f = data.failed_count || 0;
      toast.success(`${c} draft${c === 1 ? "" : "s"} created${autoPdf ? " (PDFs generated)" : ""}${f ? ` · ${f} row(s) failed` : ""}`);
      if (f) console.warn("Bulk-generate failures:", data.failed);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk generate failed");
    } finally {
      evt.target.value = "";
    }
  };
  return (
    <div className="space-y-4">
      {/* ALL-IN-ONE BULK — one Excel with 3 sheets creates Offer + Appointment + Increment in a single upload */}
      <div className="brut-card bg-emerald-50 border-emerald-400 border-2 p-4" data-testid="tpl-all-in-one-card">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-emerald-700" />
          <div>
            <div className="font-bold text-sm text-emerald-900">ALL-IN-ONE HR BULK GENERATOR</div>
            <div className="text-[10px] font-mono text-emerald-700 mt-0.5">
              One Excel with 3 sheets (Offer / Appointment / Increment) → creates every doc type per employee in a single upload. CTC in numbers + words + full break-up auto-render in the PDFs.
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => downloadTemplate("all_in_one")}
            className="brut-btn px-3 py-1.5 text-[11px] flex items-center gap-1 bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-700"
            data-testid="tpl-download-xlsx-all_in_one"
            title="Download the 3-sheet Excel template (Offer + Appointment + Increment)"
          >
            <Download className="w-3.5 h-3.5" /> DOWNLOAD ALL-IN-ONE TEMPLATE.XLSX
          </button>
          <button
            onClick={() => bulkUpload("all_in_one", false)}
            className="brut-btn px-3 py-1.5 text-[11px] flex items-center gap-1"
            data-testid="tpl-bulk-upload-all_in_one"
            title="Upload the filled 3-sheet Excel — creates drafts for each sheet's rows"
          >
            <Upload className="w-3.5 h-3.5" /> UPLOAD → DRAFTS (all 3 types)
          </button>
          <button
            onClick={() => bulkUpload("all_in_one", true)}
            className="brut-btn px-3 py-1.5 text-[11px] flex items-center gap-1 bg-amber-100 border-amber-400 hover:bg-amber-200"
            data-testid="tpl-bulk-upload-pdf-all_in_one"
            title="Upload the filled 3-sheet Excel — instantly generates all PDFs (skip review)"
          >
            <Upload className="w-3.5 h-3.5" /> UPLOAD → PDFs (skip review)
          </button>
        </div>
        <input
          ref={(el) => { uploadFilledRef.current["all_in_one"] = el; }}
          type="file"
          accept=".xlsx"
          hidden
          onChange={(e) => onUploadPicked("all_in_one", e)}
          data-testid="tpl-bulk-file-all_in_one"
        />
      </div>

      {Object.entries(byCat).map(([cat, list]) => (
        <div key={cat}>
          <div className="label-mono mb-2">{cat.toUpperCase()}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((t) => (
              <div key={t.key} className="brut-card bg-white p-3" data-testid={`tpl-card-${t.key}`}>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <div className="font-bold text-sm">{t.title}</div>
                </div>
                <p className="text-[10px] font-mono text-zinc-600 mt-1">{t.description}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="text-[9px] font-mono bg-zinc-100 border border-zinc-300 px-1.5 py-0.5">{t.fields.length} fields</span>
                  {t.retention_days_on_accept > 0 && (
                    <span className="text-[9px] font-mono bg-rose-100 border border-rose-300 text-rose-700 px-1.5 py-0.5">
                      LOCKS {t.retention_days_on_accept}d
                    </span>
                  )}
                </div>
                {BULK_KEYS.has(t.key) && (
                  <div className="mt-3 pt-3 border-t border-dashed border-zinc-300">
                    <div className="label-mono text-[9px] text-zinc-500 mb-1.5">BULK GENERATE</div>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        onClick={() => downloadTemplate(t.key)}
                        className="brut-btn px-2 py-1 text-[10px] flex items-center gap-1"
                        data-testid={`tpl-download-xlsx-${t.key}`}
                        title="Download an Excel template pre-formatted for this document type"
                      >
                        <Download className="w-3 h-3" /> TEMPLATE.XLSX
                      </button>
                      <button
                        onClick={() => bulkUpload(t.key, false)}
                        className="brut-btn px-2 py-1 text-[10px] flex items-center gap-1"
                        data-testid={`tpl-bulk-upload-${t.key}`}
                        title="Upload the filled Excel — creates drafts (HR reviews before PDF)"
                      >
                        <Upload className="w-3 h-3" /> UPLOAD → DRAFTS
                      </button>
                      <button
                        onClick={() => bulkUpload(t.key, true)}
                        className="brut-btn px-2 py-1 text-[10px] flex items-center gap-1 bg-amber-50 border-amber-300"
                        data-testid={`tpl-bulk-upload-pdf-${t.key}`}
                        title="Upload the filled Excel — instantly generates PDFs (skip review)"
                      >
                        <Upload className="w-3 h-3" /> UPLOAD → PDFs
                      </button>
                    </div>
                    <input
                      ref={(el) => { uploadFilledRef.current[t.key] = el; }}
                      type="file"
                      accept=".xlsx"
                      hidden
                      onChange={(e) => onUploadPicked(t.key, e)}
                      data-testid={`tpl-bulk-file-${t.key}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

